import React, { useCallback, useEffect, useState } from "react";
import Header from "../Homepage/sections/Header";
import Footer from "../Homepage/sections/Footer";
import { HomepageGlobalStyles } from "../Homepage/styles";
import { HOMEPAGE_ANALYSTS } from "../Homepage/constants";
import { analysts } from "../LandingPage/analystsJson";
import RegisterCheckout from "../Register/RegisterCheckout";
import {
  SALE_BENEFITS,
  SALE_COUNTDOWN_END,
  SALE_DEFAULT_COUPON,
  SALE_PLAN_ID,
  SALE_PROMO_WISTIA_ID,
} from "./saleConstants";
import {
  SaleAnalystAside,
  SaleAnalystBio,
  SaleAnalystBody,
  SaleAnalystCard,
  SaleAnalystGrid,
  SaleAnalystHead,
  SaleAnalystName,
  SaleAnalystRole,
  SaleAnalystSpecialty,
  SaleAnalystTitle,
  SaleAvatar,
  SaleAvatarWrap,
  SaleBenefitIcon,
  SaleBenefitItem,
  SaleBenefitList,
  SaleCheckoutGlow,
  SaleCheckoutGrid,
  SaleCheckoutIntro,
  SaleCheckoutLead,
  SaleCheckoutSection,
  SaleCheckoutTitle,
  SaleCheckoutWrap,
  SaleContainer,
  SaleCountdown,
  SaleCountdownLabel,
  SaleCountdownName,
  SaleCountdownSep,
  SaleCountdownUnit,
  SaleCountdownValue,
  SaleEyebrow,
  SaleHero,
  SaleHeroCta,
  SaleHeroGlow,
  SaleHeroInner,
  SaleHeroLead,
  SaleHeroRule,
  SaleHeroSubhead,
  SaleHeroTitle,
  SaleIntroBtn,
  SaleMain,
  SaleModalCard,
  SaleModalClose,
  SaleModalDesc,
  SaleModalHead,
  SaleModalScrim,
  SaleModalTitle,
  SaleOfferCard,
  SaleOfferEligibility,
  SaleOfferNote,
  SaleOfferNow,
  SaleOfferOld,
  SaleOfferPrices,
  SalePage,
  SalePitchGrid,
  SalePitchLead,
  SalePitchTitle,
  SalePlanCard,
  SalePlanEligibility,
  SalePlanMeta,
  SalePlanName,
  SalePlanPrice,
  SalePlanRenew,
  SalePlanTop,
  SalePlayIcon,
  SaleSection,
  SaleSectionGlow,
  SaleSectionRule,
  SaleVideoFrame,
  SaleVideoPlay,
  SaleVideoPoster,
  SaleVideoStage,
} from "./Sale.styles";

const SALE_ANALYSTS = HOMEPAGE_ANALYSTS.map((analyst) => {
  const source = analysts.find((item) => item.profilePic === analyst.profilePic);
  return {
    ...analyst,
    introVideo: source?.introVideo || "",
  };
});

const COUNTDOWN_UNITS = [
  { key: "days", label: "days" },
  { key: "hours", label: "hours" },
  { key: "minutes", label: "minutes" },
  { key: "seconds", label: "seconds" },
];

const PlayGlyph = () => (
  <SalePlayIcon>
    <path d="M8 5.14v13.72L19 12 8 5.14z" />
  </SalePlayIcon>
);

const pad = (value) => String(value).padStart(2, "0");

const emptyCountdown = { days: 0, hours: 0, minutes: 0, seconds: 0 };

const calcCountdown = (endDate) => {
  const remaining = Math.max(0, Date.parse(endDate) - Date.now());
  if (!Number.isFinite(remaining) || remaining <= 0) return emptyCountdown;

  return {
    days: Math.floor(remaining / 86_400_000),
    hours: Math.floor((remaining / 3_600_000) % 24),
    minutes: Math.floor((remaining / 60_000) % 60),
    seconds: Math.floor((remaining / 1_000) % 60),
  };
};

const scrollToCheckout = () => {
  document.getElementById("checkout")?.scrollIntoView({
    behavior: "smooth",
    block: "start",
  });
};

const playSaleWistia = (mediaId) => {
  if (!mediaId) return;

  const tryPlay = () => {
    const video = window.Wistia?.api?.(mediaId);
    if (!video || typeof video.play !== "function") return false;
    try {
      video.play();
      return true;
    } catch (_) {
      return false;
    }
  };

  window._wq = window._wq || [];
  window._wq.push({
    id: mediaId,
    options: {
      playButton: true,
      videoFoam: true,
      playsinline: true,
    },
    onReady: (video) => {
      try {
        video?.play?.();
      } catch (_) {
        /* ignore */
      }
    },
  });

  if (tryPlay()) return;
  window.setTimeout(tryPlay, 250);
  window.setTimeout(tryPlay, 700);
  if (window.Wistia?.embeds?.setup) {
    window.Wistia.embeds.setup();
  }
};

const Sale = () => {
  const [countdown, setCountdown] = useState(() =>
    calcCountdown(SALE_COUNTDOWN_END)
  );
  const [selectedAnalyst, setSelectedAnalyst] = useState(null);
  const [promoPlaying, setPromoPlaying] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const tick = () => setCountdown(calcCountdown(SALE_COUNTDOWN_END));
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    if (!selectedAnalyst) return undefined;

    const onKeyDown = (event) => {
      if (event.key === "Escape") setSelectedAnalyst(null);
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [selectedAnalyst]);

  useEffect(() => {
    if (!promoPlaying) return undefined;
    playSaleWistia(SALE_PROMO_WISTIA_ID);
    return undefined;
  }, [promoPlaying]);

  const openIntro = useCallback((analyst) => {
    if (analyst?.introVideo) setSelectedAnalyst(analyst);
  }, []);

  const startPromo = useCallback(() => {
    setPromoPlaying(true);
  }, []);

  return (
    <SalePage>
      <HomepageGlobalStyles />
      <Header onSubscribeClick={scrollToCheckout} hideSpacer />

      <SaleMain>
        <SaleHero>
          <SaleHeroGlow aria-hidden="true" />
          <SaleHeroRule aria-hidden="true" />
          <SaleHeroInner>
            <SaleEyebrow>One Week Only · Save 67% Today</SaleEyebrow>
            <SaleHeroTitle>
              <span>Bitcoin Live</span> — Our Bull Market Sale
            </SaleHeroTitle>
            <SaleHeroSubhead>
              From the most trusted Analysts and longest serving Crypto trading
              educational platform.
            </SaleHeroSubhead>
            <SaleHeroLead>
              The next bull market is coming! Take advantage with Bitcoin Live,
              eight years of trusted Bitcoin and crypto market education. Join
              the analysts who have navigated every bull and bear market cycle
              with composure.
            </SaleHeroLead>
            <SaleCountdownLabel>Sale ends in</SaleCountdownLabel>
            <SaleCountdown aria-label="Sale countdown">
              {COUNTDOWN_UNITS.map((unit, index) => (
                <SaleCountdownUnit key={unit.key}>
                  <div>
                    <SaleCountdownValue>
                      {pad(countdown[unit.key])}
                    </SaleCountdownValue>
                    <SaleCountdownName>{unit.label}</SaleCountdownName>
                  </div>
                  {index < COUNTDOWN_UNITS.length - 1 ? (
                    <SaleCountdownSep aria-hidden="true">:</SaleCountdownSep>
                  ) : null}
                </SaleCountdownUnit>
              ))}
            </SaleCountdown>
            <SaleHeroCta type="button" onClick={scrollToCheckout}>
              Get 3 months for $97
              <span className="material-icons" aria-hidden="true">
                south
              </span>
            </SaleHeroCta>
          </SaleHeroInner>
        </SaleHero>

        <SaleSection $muted>
          <SaleSectionRule aria-hidden="true" />
          <SaleContainer>
            <SaleAnalystHead>
              <div>
                <SaleEyebrow>The people behind the signal</SaleEyebrow>
                <SaleAnalystTitle>
                  Seven veteran analysts.
                  <br />
                  <span>One membership.</span>
                </SaleAnalystTitle>
              </div>
              <SaleAnalystAside>
                Meet the traders behind eight years of Bitcoin market education.
              </SaleAnalystAside>
            </SaleAnalystHead>

            <SaleAnalystGrid>
              {SALE_ANALYSTS.map((analyst) => (
                <SaleAnalystCard key={analyst.name}>
                  <SaleAvatarWrap>
                    <SaleAvatar
                      src={analyst.profilePic}
                      alt={`${analyst.name} — ${analyst.title}`}
                      loading="lazy"
                    />
                  </SaleAvatarWrap>
                  <SaleAnalystBody>
                    <SaleAnalystName>{analyst.name}</SaleAnalystName>
                    <SaleAnalystRole>{analyst.title}</SaleAnalystRole>
                    <SaleAnalystBio>{analyst.bio}</SaleAnalystBio>
                    <SaleAnalystSpecialty>
                      {analyst.specialty}
                    </SaleAnalystSpecialty>
                    {analyst.introVideo ? (
                      <SaleIntroBtn
                        type="button"
                        onClick={() => openIntro(analyst)}
                        aria-label={`Watch ${analyst.name}'s introduction`}
                      >
                        <PlayGlyph />
                        Watch intro
                      </SaleIntroBtn>
                    ) : (
                      <SaleIntroBtn
                        as="span"
                        aria-hidden="true"
                        style={{ visibility: "hidden" }}
                      >
                        Watch intro
                      </SaleIntroBtn>
                    )}
                  </SaleAnalystBody>
                </SaleAnalystCard>
              ))}
            </SaleAnalystGrid>
          </SaleContainer>
        </SaleSection>

        <SaleSection>
          <SaleSectionGlow aria-hidden="true" />
          <SaleContainer>
            <SalePitchGrid>
              <SaleVideoFrame>
                <SaleVideoStage
                  className={promoPlaying ? "is-playing" : undefined}
                >
                  {promoPlaying ? (
                    <div
                      className={`wistia_embed wistia_async_${SALE_PROMO_WISTIA_ID} videoFoam=true`}
                    />
                  ) : null}
                  {!promoPlaying ? (
                    <SaleVideoPoster
                      type="button"
                      onClick={startPromo}
                      aria-label="Play promo video"
                    >
                      <SaleVideoPlay>
                        <PlayGlyph />
                      </SaleVideoPlay>
                    </SaleVideoPoster>
                  ) : null}
                </SaleVideoStage>
              </SaleVideoFrame>
              <div>
                <SalePitchTitle>
                  Transform your passion into <span>trading profits.</span>
                </SalePitchTitle>
                <SalePitchLead>
                  Practical Bitcoin education and market context for newer
                  investors and experienced traders alike.
                </SalePitchLead>
                <SaleBenefitList>
                  {SALE_BENEFITS.map((item) => (
                    <SaleBenefitItem key={item}>
                      <SaleBenefitIcon>
                        <span className="material-icons" aria-hidden="true">
                          check
                        </span>
                      </SaleBenefitIcon>
                      {item}
                    </SaleBenefitItem>
                  ))}
                </SaleBenefitList>
              </div>
            </SalePitchGrid>
          </SaleContainer>
        </SaleSection>

        <SaleCheckoutSection id="checkout">
          <SaleCheckoutGlow aria-hidden="true" />
          <SaleContainer>
            <SaleOfferCard>
              <SaleEyebrow>Bull Market Sale Price</SaleEyebrow>
              <SaleOfferPrices>
                <SaleOfferOld>$297</SaleOfferOld>
                <SaleOfferNow>$97</SaleOfferNow>
              </SaleOfferPrices>
              <SaleOfferEligibility>
                For new prospective members only
              </SaleOfferEligibility>
              <SaleOfferNote>
                Three months of full access. Then renews at $297 every three
                months.
              </SaleOfferNote>
            </SaleOfferCard>

            <SaleCheckoutGrid>
              <SaleCheckoutIntro>
                <SaleCheckoutTitle>
                  Join before the clock runs out.
                </SaleCheckoutTitle>
                <SaleCheckoutLead>
                  Create your account, choose how to pay, and start your
                  three-month membership.
                </SaleCheckoutLead>
                <SalePlanCard>
                  <SalePlanTop>
                    <div>
                      <SalePlanName>Bull Market Sale access</SalePlanName>
                      <SalePlanMeta>Three months</SalePlanMeta>
                    </div>
                    <SalePlanPrice>$97</SalePlanPrice>
                  </SalePlanTop>
                  <SalePlanEligibility>
                    For new prospective members only
                  </SalePlanEligibility>
                  <SalePlanRenew>
                    Renews quarterly at the regular $297 rate. Cancel anytime.
                  </SalePlanRenew>
                </SalePlanCard>
              </SaleCheckoutIntro>

              <SaleCheckoutWrap>
                <RegisterCheckout
                  planId={SALE_PLAN_ID}
                  coupon={SALE_DEFAULT_COUPON}
                  skipScrollOnMount
                  compactHeader
                  headerAlign="left"
                  title="Complete your membership"
                  subtitle="Your account and payment details"
                  submitLabel="Join Bitcoin Live — $97"
                  termsSuffix=" and recurring quarterly renewal."
                  trustText="Secure checkout · Cancel anytime"
                  compactTrust
                />
              </SaleCheckoutWrap>
            </SaleCheckoutGrid>
          </SaleContainer>
        </SaleCheckoutSection>
      </SaleMain>

      <Footer onSubscribeClick={scrollToCheckout} />

      {selectedAnalyst ? (
        <SaleModalScrim
          role="presentation"
          onClick={() => setSelectedAnalyst(null)}
        >
          <SaleModalCard
            role="dialog"
            aria-modal="true"
            aria-labelledby="sale-intro-title"
            onClick={(event) => event.stopPropagation()}
          >
            <SaleModalClose
              type="button"
              aria-label="Close intro video"
              onClick={() => setSelectedAnalyst(null)}
            >
              <span className="material-icons" aria-hidden="true">
                close
              </span>
            </SaleModalClose>
            <div
              className={`wistia_embed wistia_async_${selectedAnalyst.introVideo}`}
            />
            <SaleModalHead>
              <SaleModalTitle id="sale-intro-title">
                Meet {selectedAnalyst.name}
              </SaleModalTitle>
              <SaleModalDesc>
                {selectedAnalyst.title} · {selectedAnalyst.specialty}
              </SaleModalDesc>
            </SaleModalHead>
          </SaleModalCard>
        </SaleModalScrim>
      ) : null}
    </SalePage>
  );
};

export default Sale;
